import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API, useAuth } from "../App";
import axios from "../lib/api";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import {
  Users, UserPlus, Trash2, Crown, User, Mail, Clock,
  ShieldCheck, RefreshCw, Send, X
} from "lucide-react";

const ROLE_LABELS = { owner: "Administrador", member: "Miembro" };
const ROLE_BADGES = {
  owner: "bg-amber-100 text-amber-800 border border-amber-200",
  member: "bg-slate-100 text-slate-700 border border-slate-200",
};

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myRole, setMyRole] = useState("member");

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, pendingRes, billingRes] = await Promise.all([
        axios.get(`${API}/org/users`, { headers: authHeader() }),
        axios.get(`${API}/org/users/invite/pending`, { headers: authHeader() }),
        axios.get(`${API}/billing/subscription-status`, { headers: authHeader() }),
      ]);
      setMembers(membersRes.data.users || []);
      setPending(pendingRes.data.invitations || []);
      setPlanInfo(billingRes.data);

      // Find my role
      const me = (membersRes.data.users || []).find(m => m.user_id === user?.user_id);
      if (me) setMyRole(me.org_role || me.role);
    } catch (err) {
      toast.error("Error al cargar el equipo");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await axios.post(
        `${API}/org/users/invite`,
        { email: inviteEmail.trim(), role: inviteRole },
        { headers: authHeader() }
      );
      toast.success(`Invitación enviada a ${inviteEmail}`);
      setInviteEmail("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar la invitación");
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm("¿Eliminar a este usuario del equipo?")) return;
    try {
      await axios.delete(`${API}/org/users/${userId}`, { headers: authHeader() });
      toast.success("Usuario eliminado del equipo");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar al usuario");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(
        `${API}/org/users/${userId}/role`,
        { role: newRole },
        { headers: authHeader() }
      );
      toast.success("Rol actualizado");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar el rol");
    }
  };

  const handleRevoke = async (token) => {
    try {
      await axios.delete(`${API}/org/users/invite/${token}`, { headers: authHeader() });
      toast.success("Invitación revocada");
      fetchData();
    } catch (err) {
      toast.error("No se pudo revocar la invitación");
    }
  };

  const isOwner = myRole === "owner";
  const maxUsers = planInfo?.plan?.features?.max_users;
  const usersUsed = members.length;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="team-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">Equipo</h1>
            <p className="text-sm text-slate-500 mt-1">
              Gestiona los miembros de tu organización
            </p>
          </div>
          <button
            onClick={fetchData}
            className="p-2 rounded-lg hover:bg-[#F0F0ED] text-slate-500 transition-colors"
            title="Actualizar"
            data-testid="team-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Plan usage bar */}
        {maxUsers && (
          <div className="bg-white rounded-xl border border-[#E8E8E4] p-4" data-testid="team-plan-usage">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-slate-700">Usuarios del plan</span>
              <span className={`text-sm font-bold ${usersUsed >= maxUsers ? "text-red-600" : "text-slate-900"}`}>
                {usersUsed} / {maxUsers}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${usersUsed >= maxUsers ? "bg-red-500" : "bg-[#C8192C]"}`}
                style={{ width: `${Math.min(100, (usersUsed / maxUsers) * 100)}%` }}
              />
            </div>
            {usersUsed >= maxUsers && (
              <p className="text-xs text-red-600 mt-2">
                Has alcanzado el límite de usuarios de tu plan. Actualiza el plan para invitar más.
              </p>
            )}
          </div>
        )}

        {/* Invite form (owner only) */}
        {isOwner && (
          <div className="bg-white rounded-xl border border-[#E8E8E4] p-5" data-testid="invite-form">
            <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#C8192C]" /> Invitar nuevo miembro
            </h2>
            <form onSubmit={handleInvite} className="flex gap-3">
              <Input
                type="email"
                placeholder="email@empresa.com"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                className="flex-1 h-9 text-sm"
                required
                data-testid="invite-email-input"
              />
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="invite-role-select"
              >
                <option value="member">Miembro</option>
                <option value="owner">Administrador</option>
              </select>
              <Button
                type="submit"
                disabled={inviting || (maxUsers && usersUsed + pending.length >= maxUsers)}
                className="h-9 bg-[#C8192C] hover:bg-[#a81424] text-white text-sm px-4"
                data-testid="invite-submit-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {inviting ? "Enviando..." : "Invitar"}
              </Button>
            </form>
          </div>
        )}

        {/* Members list */}
        <div className="bg-white rounded-xl border border-[#E8E8E4]" data-testid="members-list">
          <div className="px-5 py-4 border-b border-[#E8E8E4]">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" /> Miembros activos ({members.length})
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#C8192C]" />
            </div>
          ) : (
            <ul className="divide-y divide-[#F0F0ED]">
              {members.map(member => (
                <li key={member.user_id} className="flex items-center gap-4 px-5 py-4" data-testid={`member-row-${member.user_id}`}>
                  <div className="w-9 h-9 rounded-full bg-[#F0F0ED] flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{member.name || "—"}</p>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                      <Mail className="w-3 h-3" />{member.email}
                    </p>
                  </div>

                  {isOwner && member.user_id !== user?.user_id ? (
                    <select
                      value={member.org_role || member.role}
                      onChange={e => handleRoleChange(member.user_id, e.target.value)}
                      className="text-xs rounded border border-slate-200 px-2 py-1 bg-white text-slate-700 focus:outline-none"
                      data-testid={`member-role-select-${member.user_id}`}
                    >
                      <option value="member">Miembro</option>
                      <option value="owner">Administrador</option>
                    </select>
                  ) : (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${ROLE_BADGES[member.org_role || member.role]}`}>
                      {(member.org_role || member.role) === "owner" ? <Crown className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                      {ROLE_LABELS[member.org_role || member.role] || member.org_role || member.role}
                    </span>
                  )}

                  {isOwner && member.user_id !== user?.user_id && (
                    <button
                      onClick={() => handleRemove(member.user_id)}
                      className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      title="Eliminar miembro"
                      data-testid={`remove-member-${member.user_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pending invitations (owner only) */}
        {isOwner && pending.length > 0 && (
          <div className="bg-white rounded-xl border border-[#E8E8E4]" data-testid="pending-invitations">
            <div className="px-5 py-4 border-b border-[#E8E8E4]">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" /> Invitaciones pendientes ({pending.length})
              </h2>
            </div>
            <ul className="divide-y divide-[#F0F0ED]">
              {pending.map(inv => (
                <li key={inv.invitation_id} className="flex items-center gap-4 px-5 py-4" data-testid={`pending-invite-${inv.invitation_id}`}>
                  <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{inv.email}</p>
                    <p className="text-xs text-slate-500">
                      {ROLE_LABELS[inv.role] || inv.role} · Expira {new Date(inv.expires_at).toLocaleDateString("es-ES")}
                    </p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    Pendiente
                  </span>
                  <button
                    onClick={() => handleRevoke(inv.invitation_id)}
                    className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    title="Revocar invitación"
                    data-testid={`revoke-invite-${inv.invitation_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
